/**
 * Region-aware address provider.
 *
 * BR: Uses ViaCEP API for postal code auto-fill + IBGE for states/cities.
 * LB: Manual address entry (no postal code API).
 */
import regionConfig from '../config/regionConfig';

/**
 * Look up address by postal code.
 * Returns null if the region doesn't support postal code lookup.
 * @param {string} postalCode - The postal code to look up
 * @param {object} api - The axios api instance
 * @returns {Promise<object|null>} Address data or null
 */
export async function lookupPostalCode(postalCode, api) {
  if (regionConfig.address.provider === 'viacep') {
    try {
      const response = await api.get(`/api/v1/ibge/cep/${postalCode}`);
      if (response.data) {
        return {
          street: response.data.logradouro || '',
          neighborhood: response.data.bairro || '',
          city: response.data.localidade || '',
          state: response.data.uf || '',
        };
      }
    } catch (error) {
      console.warn('[ADDRESS] CEP lookup failed:', error.message);
    }
    return null;
  }

  // Manual provider: no automatic lookup
  return null;
}

/**
 * Check if postal code auto-lookup is available.
 * @returns {boolean}
 */
export function hasPostalCodeLookup() {
  return regionConfig.address.provider !== 'manual';
}

/**
 * Get label for postal code field.
 * @returns {string} "CEP" or "Postal Code"
 */
export function getPostalCodeLabel() {
  return regionConfig.address.postalCodeName;
}

/**
 * Get input mask for postal code.
 * @returns {string} e.g. "99999-999" or "99999999"
 */
export function getPostalCodeMask() {
  return regionConfig.address.postalCodeMask;
}

/**
 * Get label for subdivision field (state/governorate).
 * @returns {string} "Estado" or "Governorate"
 */
export function getSubdivisionLabel() {
  return regionConfig.address.subdivisionLabel;
}

/**
 * Get label for city field.
 * @returns {string} "Municipio" or "City"
 */
export function getCityLabel() {
  return regionConfig.address.cityLabel;
}

/**
 * Get label for neighborhood field.
 * @returns {string} "Bairro" or "Area"
 */
export function getNeighborhoodLabel() {
  return regionConfig.address.neighborhoodLabel;
}
